import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Input } from '@voilajsx/uikit/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@voilajsx/uikit/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@voilajsx/uikit/dialog';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Label } from '@voilajsx/uikit/label';
import { BarChart3, Edit, Loader2, Shield } from 'lucide-react';
import { AdminSidebar } from '../components/AdminSidebar';
import { Header, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

interface Service {
  code: string;
  name: string;
  description: string;
  category: string;
  status: 'active' | 'inactive';
  tier1Price: number;
  tier2Price: number;
  sortOrder: number;
}

const AdminServicesPage: React.FC = () => {
  const { user, token } = useAuth();

  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [tier1Price, setTier1Price] = useState(0);
  const [tier2Price, setTier2Price] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';
  const isModeratorManage = user?.role === 'moderator' && user?.level === 'manage';
  const hasAccess = isAdminSystem || isModeratorManage;

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchServices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/service/admin/list`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setServices(data.data?.services || data.services || data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch services');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess && token) fetchServices();
  }, [token, hasAccess]);

  if (!user) return null;

  const openEditDialog = (service: Service) => {
    setEditingService(service);
    setTier1Price(service.tier1Price);
    setTier2Price(service.tier2Price);
    setDialogOpen(true);
  };

  const handleSavePricing = async () => {
    if (!editingService) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/service/admin/${editingService.code}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          name: editingService.name,
          description: editingService.description,
          category: editingService.category,
          tier1Price,
          tier2Price,
          sortOrder: editingService.sortOrder,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}: ${res.statusText}`);
      }
      setSuccess(`Pricing updated for ${editingService.name}`);
      setDialogOpen(false);
      fetchServices();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update pricing');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (service: Service) => {
    const newStatus = service.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${config.api.baseUrl}/api/service/admin/${service.code}/status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to toggle status');
      setSuccess(`${service.name} ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchServices();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!hasAccess) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">You don't have permission to access this page.</p>
                  <Button asChild><a href={route('/dashboard')}>Back to Dashboard</a></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout size="full">
      <SEO title="Services & Pricing" description="Manage service pricing" />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Services & Pricing</h1>
                <p className="text-muted-foreground">Manage pricing and availability of services</p>
              </div>

              {success && (
                <Alert className="bg-green-950 border-green-800 text-green-300">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert className="bg-destructive/10 border-destructive text-destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Services ({services.length})
                  </CardTitle>
                  <CardDescription>All registered services and their configurations</CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-2 py-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-12 rounded-md bg-zinc-800 animate-pulse" />
                      ))}
                    </div>
                  ) : services.length === 0 ? (
                    <div className="text-center py-12">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-lg font-medium mb-2">No services found</p>
                    </div>
                  ) : (
                    <div className="rounded-md border border-zinc-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-zinc-700">
                            <TableHead className="border-r border-zinc-700">Code</TableHead>
                            <TableHead className="border-r border-zinc-700">Name</TableHead>
                            <TableHead className="border-r border-zinc-700">Category</TableHead>
                            <TableHead className="border-r border-zinc-700">Status</TableHead>
                            {isAdminSystem && <TableHead className="border-r border-zinc-700 text-right">New (₹)</TableHead>}
                            {isAdminSystem && <TableHead className="border-r border-zinc-700 text-right">Re-download (₹)</TableHead>}
                            {isAdminSystem && <TableHead className="w-[140px]">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {services.map((service) => (
                            <TableRow key={service.code} className="border-b border-zinc-700">
                              <TableCell className="border-r border-zinc-700 font-mono text-sm">{service.code}</TableCell>
                              <TableCell className="border-r border-zinc-700">
                                <div>
                                  <div className="font-medium">{service.name}</div>
                                  {service.description && (
                                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">{service.description}</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="border-r border-zinc-700">
                                <Badge variant="secondary">{service.category}</Badge>
                              </TableCell>
                              <TableCell className="border-r border-zinc-700">
                                <Badge variant={service.status === 'active' ? 'default' : 'destructive'}>
                                  {service.status}
                                </Badge>
                              </TableCell>
                              {isAdminSystem && (
                                <TableCell className="border-r border-zinc-700 text-right">
                                  <span className="font-bold text-[#FFD93D]">₹{service.tier1Price}</span>
                                </TableCell>
                              )}
                              {isAdminSystem && (
                                <TableCell className="border-r border-zinc-700 text-right">
                                  <span className="font-bold text-muted-foreground">₹{service.tier2Price}</span>
                                </TableCell>
                              )}
                              {isAdminSystem && (
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit Pricing" onClick={() => openEditDialog(service)}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleStatus(service)}
                                      className="text-xs px-2"
                                      style={{ color: service.status === 'active' ? '#ef4444' : '#22c55e' }}
                                    >
                                      {service.status === 'active' ? 'Deactivate' : 'Activate'}
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </PageLayout.Content>

      {/* Edit Pricing Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm bg-[#1e1e1e] border-zinc-700 text-foreground">
          <DialogHeader>
            <DialogTitle>Edit Pricing</DialogTitle>
            <DialogDescription>
              Update pricing for <span className="font-semibold text-foreground">{editingService?.name}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tier1Price">New Download (₹)</Label>
              <Input
                id="tier1Price"
                type="number"
                min="0"
                value={tier1Price}
                onChange={(e) => setTier1Price(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">Credits deducted for first-time download</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tier2Price">Re-download (₹)</Label>
              <Input
                id="tier2Price"
                type="number"
                min="0"
                value={tier2Price}
                onChange={(e) => setTier2Price(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">Credits deducted for re-downloading same design</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleSavePricing} disabled={isSaving} className="gap-2">
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminServicesPage;
